import * as React from 'react';
import styled from 'styled-components';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BarChartIcon from '@material-ui/icons/BarChart';
import CampaignIcon from '@material-ui/icons/Announcement';

import DailyMessage from '../organisms/Dashboard/DailyMessage';
import InspectorMotdComposer from '../organisms/Dashboard/InspectorMotdComposer';
import InspectionTasksBlock from '../molecules/InspectionTasksBlock';
import CompletedInspectionsGraph from '../organisms/CompletedInspectionsGraph';
import MobileInspectionGraph from '../organisms/MobileInspectionGraph/MobileInspectionGraph';

const SHOW_STORAGE_KEY = 'inspectorDashboard.showDashboardBlocks';

const InspectorDashboardBlocks = () => {
  // Bumped after the designated inspector sends, so the list below re-reads.
  const [messagesUpdate, setMessagesUpdate] = React.useState(0);
  const [showDashboardBlocks, setShowDashboardBlocks] = React.useState<boolean>(() => {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(SHOW_STORAGE_KEY) : null;
    return stored === null ? true : stored === 'true';
  });

  const toggleShow = () => {
    const next = !showDashboardBlocks;
    setShowDashboardBlocks(next);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SHOW_STORAGE_KEY, String(next));
    }
  };

  return (
    <Page>
      <DashboardToggleRow>
        <DashboardToggleButton type="button" onClick={toggleShow} aria-expanded={showDashboardBlocks}>
          <Chevron $open={showDashboardBlocks}>▾</Chevron>
          {showDashboardBlocks ? 'Hide dashboard' : 'Show dashboard'}
        </DashboardToggleButton>
      </DashboardToggleRow>

      {showDashboardBlocks && (
        <Wrapper>
          <Card>
            <CardHeader>
              <TitleRow>
                <TitleIcon $bg="#E9F2FF" $fg="#2563EB">
                  <AssignmentIcon />
                </TitleIcon>
                <TitleText>
                  <Eyebrow>Overview</Eyebrow>
                  <Title>My Tasks</Title>
                </TitleText>
              </TitleRow>
            </CardHeader>
            <CardBody>
              <InspectionTasksBlock />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <TitleRow>
                <TitleIcon $bg="#DCFCE7" $fg="#16A34A">
                  <BarChartIcon />
                </TitleIcon>
                <TitleText>
                  <Eyebrow>Activity</Eyebrow>
                  <Title>Completed Inspections</Title>
                </TitleText>
              </TitleRow>
            </CardHeader>
            <CardBody>
              <CompletedInspectionsGraph />
              <MobileInspectionGraph />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <TitleRow>
                <TitleIcon $bg="#F3E8FF" $fg="#9333EA">
                  <CampaignIcon />
                </TitleIcon>
                <TitleText>
                  <Eyebrow>Updates</Eyebrow>
                  <Title>Message of the day</Title>
                </TitleText>
              </TitleRow>
            </CardHeader>
            <CardBody>
              <InspectorMotdComposer
                onSent={() => setMessagesUpdate(value => value + 1)}
              />
              <DailyMessage hideHeader fillParent manuallyUpdate={messagesUpdate} />
            </CardBody>
          </Card>
        </Wrapper>
      )}
    </Page>
  );
};

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Page = styled.div`
  padding: 20px 0 0;
  box-sizing: border-box;
  width: 100%;
  font-family: ${FONT_STACK};

  @media (max-width: 840px) {
    padding: 14px 0 0;
  }

  @media (max-width: 540px) {
    padding: 10px 0 0;
  }
`;

const DashboardToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const DashboardToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  color: var(--neutral-500);
`;

const Wrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(360px, 1.5fr) minmax(260px, 1fr);
  gap: 20px;
  align-items: stretch;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;

    & > :nth-child(3) {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 1150px) {
    grid-template-columns: 1fr;
    gap: 14px;

    & > :nth-child(3) {
      grid-column: auto;
    }
  }
`;

const Card = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-xs);
  font-family: ${FONT_STACK};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: var(--neutral-300);
    box-shadow: var(--shadow-sm, 0 2px 6px rgba(15, 23, 42, 0.08));
  }
`;

const CardHeader = styled.div`
  padding: 14px 18px;
  background: var(--neutral-0);
  border-bottom: 1px solid var(--border);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const TitleIcon = styled.div<{ $bg: string; $fg: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 20px;
  }
`;

const TitleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Eyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  line-height: 1.2;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardBody = styled.div`
  padding: 18px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export default InspectorDashboardBlocks;
